import { SelectField } from '@axinom/mosaic-ui';
import { useFormikContext } from 'formik';
import React, { type ChangeEvent } from 'react';
import {
  ConflictIcon,
  CreateIcon,
  OverwriteIcon,
  ProtectedIcon,
  SkipIcon,
  UnprotectedIcon,
} from './ServiceAccountImportIcons';
import classes from './ServiceAccountImportPreview.module.scss';
import {
  ImportActions,
  type ImportFormData,
  type ImportSummary,
  resolutionOptions,
} from './ServiceAccountImportPreview.types';

export const conflictsCountRenderer = (val: unknown): React.ReactElement => {
  const count = val as number;
  return (
    <div className={classes.iconText}>
      {count > 0 ? (
        <>
          <ConflictIcon className={classes.icon} />
          <span className={classes.text}>
            {count} service accounts already exist
          </span>
        </>
      ) : (
        <span className={classes.text}>No Conflicts</span>
      )}
    </div>
  );
};

const ConflictResolutionSelect: React.FC<{ row: ImportSummary }> = ({
  row,
}) => {
  const { values, setFieldValue } = useFormikContext<ImportFormData>();
  return (
    <SelectField
      name="conflictResolution"
      value={values.conflictResolution}
      onChange={(event: ChangeEvent<HTMLInputElement>) => {
        // shouldValidate: false - the resolution's own change should not
        // validate itself, since hasUnresolvedConflicts/hasNothingToImport
        // haven't caught up to this new value yet. Letting only their own
        // sync effects (which run after the preview recomputes) trigger
        // validation avoids two overlapping, out-of-order validation passes.
        void setFieldValue(
          'conflictResolution',
          event.currentTarget.value,
          false,
        );
      }}
      options={resolutionOptions}
      inlineMode={true}
      disabled={row.conflicts < 1}
    />
  );
};

export const conflictResolutionRenderer = (
  _val: unknown,
  row: ImportSummary,
): React.ReactElement => <ConflictResolutionSelect row={row} />;

export const protectionRenderer = (value: unknown): React.ReactElement =>
  (value as boolean) ? (
    <div className={classes.iconText}>
      <ProtectedIcon className={classes.icon} />
      <span className={classes.text}>Protected</span>
    </div>
  ) : (
    <div className={classes.iconText}>
      <UnprotectedIcon className={classes.icon} />
      <span className={classes.text}>Unprotected</span>
    </div>
  );

export const importActionRenderer = (value: unknown): React.ReactElement => {
  let content: React.ReactElement;
  switch (value as ImportActions) {
    case ImportActions.Conflict: {
      content = (
        <div className={classes.iconText}>
          <ConflictIcon className={classes.icon} />
          <span className={classes.text}>Conflict</span>
        </div>
      );
      break;
    }
    case ImportActions.Create: {
      content = (
        <div className={classes.iconText}>
          <CreateIcon className={classes.icon} />
          <span className={classes.text}>Create</span>
        </div>
      );
      break;
    }

    case ImportActions.Overwrite: {
      content = (
        <div className={classes.iconText}>
          <OverwriteIcon className={classes.icon} />
          <span className={classes.text}>Overwrite</span>
        </div>
      );
      break;
    }

    case ImportActions.Skip: {
      content = (
        <div className={classes.iconText}>
          <SkipIcon className={classes.icon} />
          <span className={classes.text}>Skip</span>
        </div>
      );
      break;
    }
  }
  return content;
};
